import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import { cn } from "@core-ui/utils/cn";
import t from "@ext/localization/locale/translate";
import BlockCommentView from "@ext/markdown/elements/comment/edit/components/View/BlockCommentView";
import Group from "@ext/markdown/elements/view/render/components/Displays/Helpers/Table/Group";
import PropertyServiceProvider from "@ext/properties/components/PropertyService";
import type { ViewRenderGroup } from "@ext/properties/models";
import { stickyCellStyles, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ui-kit/Table";

// The kit sets `px-3 py-2.5` on its cells against `text-xs`; restated in em it means the same
// thing while following the font size the host sets, like the em-based displays around it.
const CELL_PADDING = "[&_:is(th,td)]:px-[1em] [&_:is(th,td)]:py-[0.8333em]";

interface TableNewProps {
	content: ViewRenderGroup[];
	groupby: string[];
	select: string[];
	className?: string;
	commentId?: string;
}

const TableNew = ({ content, className, groupby, select, commentId }: TableNewProps) => {
	const catalogName = useCatalogPropsStore((state) => state.data?.name);
	const { properties } = PropertyServiceProvider.value;
	const [firstColumn, ...otherColumns] = groupby.length ? groupby : [null];

	return (
		<BlockCommentView commentId={commentId}>
			<Table
				className={cn("article-links", CELL_PADDING, className)}
				containerClassName="max-h-full"
				data-focusable="true"
			>
				<TableHeader sticky>
					<TableRow>
						<TableHead className={stickyCellStyles({ part: "head", className: "left-0" })} scope="col">
							{firstColumn ? properties.get(firstColumn)?.name || firstColumn : t("properties.article")}
						</TableHead>
						{otherColumns.map((name) => (
							<TableHead key={`groupby-${name}`} scope="col">
								{properties.get(name)?.name || name}
							</TableHead>
						))}
						{groupby.length > 0 && <TableHead scope="col">{t("properties.article")}</TableHead>}
						{select.map((name) => (
							<TableHead key={name} scope="col">
								{properties.get(name)?.name || name}
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody>
					{content.map((group) => (
						<Group
							Cell={TableCell}
							catalogName={catalogName}
							catalogProperties={properties}
							firstColumnClassName={stickyCellStyles({ className: "left-0" })}
							group={group}
							groupCount={groupby.length}
							key={group.group?.[0]}
							Row={TableRow}
							select={select}
						/>
					))}
				</TableBody>
			</Table>
		</BlockCommentView>
	);
};

export default TableNew;
